import {attr, controller, target, targets} from '@github/catalyst'
import {currencyFormatter, getUnitData} from './utils'
import type {UnitData} from './utils'

@controller
export class CopilotCalculatorElement extends HTMLElement {
  static TOTAL_COST_PER_MONTH = 'Total cost per month'
  static OVERAGE_REQUESTS = 'overage_requests'

  @targets declare plans: HTMLInputElement[]
  @targets declare planLabels: HTMLElement[]
  @targets declare developerCountInputs: HTMLInputElement[]
  @target declare costPerMonthDisplay: HTMLElement

  get selectedPlan() {
    return this.plans.find(planOption => planOption.checked)
  }

  get plan() {
    return (this.selectedPlan && this.selectedPlan.value) || ''
  }

  get planPrice() {
    if (!this.unit_data) {
      this.unit_data = getUnitData()
      if (!this.unit_data) return 0
    }

    const planData = this.unit_data[this.plan]
    if (!planData?.value) return 0

    return planData.value
  }

  getDeveloperCount(planType: string): number {
    const inputElement = this.developerCountInputs.find(input => input.getAttribute('data-plan-type') === planType)
    if (!inputElement) return 0

    const value = inputElement.value.trim()
    if (!value) return 0

    const count = parseInt(value)
    return !isNaN(count) && count > 0 ? count : 0
  }

  @attr costPerMonth = 0
  @attr active = false

  declare unit_data: UnitData

  connectedCallback() {
    // Load unit data as soon as element is connected
    this.unit_data = getUnitData()

    if (this.selectedPlan) {
      this.updateCost()
    }

    // Watch for section activation
    const section = this.closest('.calculator-section')
    if (section) {
      const observer = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          if (mutation.target instanceof HTMLElement && mutation.attributeName === 'class') {
            const isHidden = mutation.target.classList.contains('calculator-section--hidden')
            if (!isHidden) {
              this.active = true
              this.updateCost()
            }
          }
        }
      })
      observer.observe(section, {
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true,
      })
    }
  }

  planUpdated(event: Event) {
    this.setAttribute('data-active', 'true')

    for (const planLabel of this.planLabels) {
      planLabel.classList.remove('gradient-border')
    }
    const plan = event.currentTarget as HTMLInputElement
    const label = plan.closest('label')
    if (label) {
      label.classList.add('gradient-border')
    }

    // Clear all developer count inputs when switching plans
    for (const input of this.developerCountInputs) {
      input.value = ''
    }

    this.updateCost()
  }

  developerCountChanged(event: Event) {
    this.setAttribute('data-active', 'true')
    const input = event.target as HTMLInputElement
    const planType = input.getAttribute('data-plan-type')
    const value = input.value.trim()
    const count = parseInt(value)

    // Clear invalid values and provide feedback
    if (value !== '' && (isNaN(count) || count < 1)) {
      input.value = ''
      input.setAttribute('aria-invalid', 'true')
    } else {
      input.removeAttribute('aria-invalid')
    }

    // If entering developer count for organization plan, deselect individual plans
    if (planType && planType !== 'overage' && value.trim() !== '') {
      for (const plan of this.plans) {
        plan.checked = false
      }
      for (const label of this.planLabels) {
        label.classList.remove('gradient-border')
      }
    }

    this.costPerMonthDisplay.setAttribute('aria-live', 'polite')
    this.calculateAndUpdateCost()
  }

  calculateAndUpdateCost() {
    let totalCost = 0

    // Handle individual plans when selected
    if (this.selectedPlan) {
      const planData = this.unit_data[this.plan]
      const isIndividualPlan =
        this.plan === 'copilot_pro' || this.plan === 'copilot_free' || this.plan === 'copilot_pro_plus'

      if (isIndividualPlan) {
        totalCost = planData?.value || 0
      }
    }

    // Always calculate overage costs and organization plans
    for (const input of this.developerCountInputs) {
      const planType = input.getAttribute('data-plan-type')
      const value = input.value.trim()

      if (!planType || !value) continue

      const count = parseInt(value)
      if (isNaN(count) || count < 1) continue

      if (planType === 'overage') {
        // Add $0.04 per request for overage
        totalCost += count * 4
      } else {
        // Only add org plan costs if no individual plan is selected
        if (!this.selectedPlan) {
          const orgPlanData = this.unit_data[planType]
          if (!orgPlanData?.value) continue

          const planCost = count * orgPlanData.value
          totalCost += planCost
        }
      }
    }

    this.costPerMonth = totalCost
    const formattedCost = currencyFormatter.format(totalCost / 100)
    this.costPerMonthDisplay.textContent = formattedCost

    // Only dispatch events if section is active
    if (this.active) {
      document.dispatchEvent(
        new CustomEvent('subtotalChanged', {
          detail: {sectionName: 'copilotTotal', amount: totalCost, active: true},
        }),
      )

      document.dispatchEvent(
        new CustomEvent('formDataUpdated', {
          detail: {
            sectionName: 'copilot',
            resetEnabled: totalCost > 0,
          },
        }),
      )
    }
  }

  updateCost = this.calculateAndUpdateCost

  resetButtonClicked(event: PointerEvent) {
    event.preventDefault()
    for (const input of this.developerCountInputs) {
      input.value = ''
    }
    for (const plan of this.plans) {
      plan.checked = false
    }
    for (const planLabel of this.planLabels) {
      planLabel.classList.remove('gradient-border')
    }
    this.costPerMonth = 0
    this.updateCost()
  }

  get costPerDevPerMonth() {
    // For individual plans, use planPrice
    if (this.selectedPlan) {
      const isIndividualPlan =
        this.plan === 'copilot_pro' || this.plan === 'copilot_free' || this.plan === 'copilot_pro_plus'
      if (isIndividualPlan) {
        return this.planPrice
      }
    }

    // For organization plans, calculate total cost divided by total developers
    let totalDevelopers = 0
    let totalCost = 0

    for (const input of this.developerCountInputs) {
      const planType = input.getAttribute('data-plan-type')
      const value = input.value.trim()
      if (!planType || !value) continue

      const count = parseInt(value)
      if (isNaN(count) || count < 1) continue

      if (planType === 'overage') {
        totalCost += count * 4 // 4 cents per request
      } else {
        const orgPlanData = this.unit_data[planType]
        if (!orgPlanData?.value) continue

        totalDevelopers += count
        totalCost += count * orgPlanData.value
      }
    }

    return totalDevelopers > 0 ? totalCost / totalDevelopers : 0
  }

  getSummaryForCSV() {
    const details = this.developerCountInputs
      .map(input => {
        const planType = input.getAttribute('data-plan-type')
        const count = this.getDeveloperCount(planType || '')
        if (!count) return null

        if (planType === 'overage') {
          return `${CopilotCalculatorElement.OVERAGE_REQUESTS}: ${count}`
        }

        const planData = this.unit_data[planType || '']
        if (!planData) return null
        return `${planData.name || planType}: ${count} developers`
      })
      .filter(Boolean)
      .join(', ')

    return [
      [
        'GitHub Copilot',
        `"${details}"`,
        `"Cost Per Developer Per Month: ${currencyFormatter.format(this.costPerDevPerMonth / 100)}"`,
        `"${currencyFormatter.format(this.costPerMonth / 100)}"`,
      ],
    ]
  }

  static observedAttributes = ['data-active']
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return

    if (name === 'data-active') {
      this.active = newValue === 'true' || newValue === ''

      if (!this.active) {
        document.dispatchEvent(
          new CustomEvent('subtotalChanged', {
            detail: {sectionName: 'copilotTotal', amount: 0, active: false},
          }),
        )
      } else {
        this.updateCost()
      }
    }
  }
}
